/* eslint-disable no-prototype-builtins */
import { Injectable } from '@angular/core';
import { FilterTypes } from '@models/filterEnums.model';
import { StudySearchFilter } from '../../../models/studySearchFilter.model';
import { FilterCriteria } from './filter-criteria.model';
import { Lookup } from '../../../models/lookup.model';
import { IdentificationNumbersCriteria } from '../filter-identification-number/filter-identification-number.model';
import { YearOfIncorporationCriteria } from '../filter-year-of-incorporation/filter-year-of-incorporation.model';
import { CategoryOfCompanyCriteria } from '../filter-category-of-company/filter-category-of-company.model';
import { WebsiteCriteria } from '../filter-website/filter-website.model';
import { StatusCriteria } from '../filter-status/filter-status.model';
import { LocationCriteria } from '../filter-location/filter-location.model';
import { LegalFormCriteria } from '../filter-legal-form/filter-legal-form.model';
import { AccountTypesCriteria } from '../filter-account-types/filter-account-types.model';
import { NumberOfEmployeesCriteria } from '../filter-number-of-employees/filter-number-of-employees.model';
import { IndustryClassificationsCriteria } from '../filter-industry-classifications/filter-industry-classifications.model';
import { BVDIndependenceIndicatorCriteria } from '../filter-bvdindependence-indicator/filter-bvdindependence-indicator.model';
import { ShareholderCharacteristicsCriteria } from '../filter-shareholder-characteristics/filter-shareholder-characteristics.model';
import { SubsidiaryCharacteristicsCriteria } from '../filter-subsidiary-characteristics/filter-subsidiary-characteristics.model';
import { FinancialCriteria } from '../filter-financial/filter-financial.model';
import { CompanyNameFilter } from '../filter-company-name/filter-company-name.model';
import { KeywordsFilter } from '../filter-keywords/filter-keywords.model';
import { ListedUnlistedFilter } from '../filter-listed-unlisted/filter-listed-unlisted.model';
import { LoggingService } from '../../../services/logging.service';

@Injectable({
  providedIn: 'root',
})
export class SearchFilterService {
  constructor(private logger: LoggingService) {}

  public generateFilterCriteriaForSearchFilters(filters: StudySearchFilter[]) {
    let displayOrder = 1;
    if (!!filters && filters.length > 0) {
      filters.forEach((f) => {
        const filterCriteria = this.convertFromStudySearchFilter(f);
        if (filterCriteria !== null) {
          filterCriteria.order = displayOrder;

          f.filter = filterCriteria;
          f.displayOrder = displayOrder;
          f.stepFilterJson = JSON.stringify(f.filter);

          displayOrder++;
        }
      });
    }
  }

  public convertFromFilterCriteriaAndType(filterCriteria: FilterCriteria, filterType: Lookup): StudySearchFilter {
    const filter = new StudySearchFilter();

    filter.stepFilterType = filterType;
    filter.stepFilterTypeId = filterType.lookupId;

    if (filterCriteria.type == 'BVDIndependenceIndicator') {
      const sorted = this.sort('data.value', filterCriteria['bvdIndependence']);
      filterCriteria['bvdIndependence'] = sorted;
    }

    filter.stepFilterJson = JSON.stringify(filterCriteria, (key, value) => {
      // remove parent property in TreeNode to prevent circular reference
      if (value && value.hasOwnProperty('parent')) {
        const clone = Object.assign({}, value);
        delete clone.parent;
        return clone;
      }
      return value;
    });

    return filter;
  }

  public changeFilterCriteriaProperty(filterCriteria: FilterCriteria): FilterCriteria {
    const FilterTypesArray: string[] = [FilterTypes.Status];

    if (FilterTypesArray.indexOf(filterCriteria.type) > -1) {
      const filter = this.getFilterObject(filterCriteria.type); // new FilterCriteria();
      const val = JSON.stringify(filterCriteria, (key, value) => {
        // remove parent property in TreeNode to prevent circular reference
        if (value && value.hasOwnProperty('parent')) {
          const clone = Object.assign({}, value);
          delete clone.parent;
          return clone;
        }
        return value;
      });
      if (filter) {
        filter.copyInto(val);
        if (filterCriteria.order) {
          filter.order = filterCriteria.order;
        }
      }
      return filter;
    }

    return filterCriteria;
  }

  private sort(valuePath, array): any {
    const path = valuePath.split('.');

    return array.sort((a, b) => {
      return this.getValue(a, path) - this.getValue(b, path);
    });
  }

  private getValue(obj, path): any {
    path.forEach((path) => (obj = obj[path]));
    return obj;
  }

  public getFilterObject(filterType: string): FilterCriteria {
    switch (filterType) {
      case FilterTypes.IdentificationNumber: {
        return new IdentificationNumbersCriteria();
      }
      case FilterTypes.YearOfIncorporation: {
        return new YearOfIncorporationCriteria();
      }
      case FilterTypes.CategoryOfCompany: {
        return new CategoryOfCompanyCriteria();
      }
      case FilterTypes.Website: {
        return new WebsiteCriteria();
      }
      case FilterTypes.Status: {
        return new StatusCriteria();
      }
      case FilterTypes.Location: {
        return new LocationCriteria();
      }
      case FilterTypes.LegalForm: {
        return new LegalFormCriteria();
      }
      case FilterTypes.AccountType: {
        return new AccountTypesCriteria();
      }
      case FilterTypes.NumberOfEmployees: {
        return new NumberOfEmployeesCriteria();
      }
      case FilterTypes.IndustryClassifications: {
        return new IndustryClassificationsCriteria();
      }
      case FilterTypes.BVDIndependenceIndicator: {
        return new BVDIndependenceIndicatorCriteria();
      }
      case FilterTypes.ShareholderCharacteristics: {
        return new ShareholderCharacteristicsCriteria();
      }
      case FilterTypes.SubsidiaryCharacteristics: {
        return new SubsidiaryCharacteristicsCriteria();
      }
      case FilterTypes.Financial: {
        return new FinancialCriteria();
      }
      case FilterTypes.CompanyName: {
        return new CompanyNameFilter();
      }
      case FilterTypes.Keywords: {
        return new KeywordsFilter();
      }
      case FilterTypes.ListedUnlisted: {
        return new ListedUnlistedFilter();
      }
      default: {
        this.logger.error(this.constructor.name, 'Error', 'Filter Type not found:', filterType);
        return null;
      }
    }
  }

  private convertFromStudySearchFilter(searchStrategy: StudySearchFilter): FilterCriteria {
    if (searchStrategy.stepFilterType) {
      const filter = this.getFilterObject(searchStrategy.stepFilterType.name);
      if (filter) {
        filter.copyInto(searchStrategy.stepFilterJson);
        return filter;
      }
    }
    return null;
  }
}
